import { ErrorBoundary } from "@/components/common/ErrorBoundary"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { normalizeSheetName } from "@/utils/urls.const"
import { FortuneFile } from "@corbe30/fortune-excel/dist/ToFortuneSheet/FortuneFile"
import { HandleZip } from "@corbe30/fortune-excel/dist/ToFortuneSheet/HandleZip"
import { Workbook, WorkbookInstance } from "@fortune-sheet/react"
import "@fortune-sheet/react/dist/index.css"
import * as Sentry from "@sentry/react"
import { decode } from "html-entities"
import {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { fetchWithRetry } from "./fetchWithRetry"
import { type FortuneFrozen, readFrozenPanes } from "./frozenPanes"
import { landOnRange } from "./landOnRange"
import {
  applyRangeDecorations,
  type RangeDecoration,
  type WorkbookRange,
} from "./rangeDecorations"

type FileEvent = React.ChangeEvent<HTMLInputElement>

/**
 * Put a range on screen: activate its sheet, optionally select it, scroll to it.
 * A FRESH OBJECT is a fresh jump — the viewer keys on identity, so asking twice
 * for the same range works.
 */
export type WorkbookJump = {
  /** Sheet *name*, as the workbook spells it (same coordinates as a decoration). */
  sheet: string
  range: WorkbookRange
  /**
   * Select the range as well as scrolling to it. The native selection ring is
   * freeze-correct and reads as "you are here", so it is the ACTIVE PROOF's
   * cursor; a search hit only scrolls, and is marked by its decoration instead.
   * Defaults to true.
   */
  select?: boolean
}

/**
 * One surface of the loaded workbook, in the file's own order — the viewer's
 * reading of the sheet list, which a caller can use to map a persisted surface
 * ordinal to a sheet name (and to notice honestly when it cannot).
 */
export type WorkbookSurface = {
  /** 1-based, like a page. */
  ordinal: number
  name: string
  /** The supplier had hidden this sheet. It is still shown (see `revealHidden`). */
  hidden: boolean
}

// The Workbook component's own Sheet type and the transform's IfortuneSheet
// describe the same runtime shape but are structurally incompatible unions;
// this alias lets the single cast at the <Workbook data=…> boundary stay typed.
type WorkbookData = React.ComponentProps<typeof Workbook>["data"]

// The empty toolbar and menus, as ONE stable array. The renderer rebuilds its
// settings (a deep clone of its defaults) whenever a prop changes identity, and
// the rebuild re-runs its init effect, which redraws the whole canvas — so an
// inline `[]` here cost a full redraw on every render of this component.
const NO_ITEMS: string[] = []

type TransformStage = "fetch" | "arrayBuffer" | "unzip" | "parse" | "serialize"

// Structural types for the Fortune Sheet data passing through this viewer.
// All fields are optional so both the upstream transform's IfortuneSheet
// objects and minimal test fixtures satisfy them; only the fields this file
// actually reads are typed.
type FortuneCellValue = { v?: unknown; mc?: unknown; ct?: unknown }
type FortuneCell = {
  r?: number
  c?: number
  v?: FortuneCellValue | string | null
}
type FortuneSheetConfig = {
  columnlen?: Record<string, number>
  rowlen?: Record<string, number>
  customWidth?: Record<string, number>
  rowhidden?: Record<string, number>
  colhidden?: Record<string, number>
  images?: unknown
}
type FortuneSheet = {
  id?: string
  name?: string
  celldata?: FortuneCell[]
  config?: FortuneSheetConfig
  defaultColWidth?: number
  zoomRatio?: number
  images?: unknown
  /** 1 = the workbook hides this sheet's tab. */
  hide?: number
  frozen?: FortuneFrozen
}

type TransformDiagnostics = {
  sourceType: "url" | "file"
  fileName: string
  bufferBytes?: number
  sheetCount?: number
  sanitizedValidationOperators?: number
}

const createStageError = (
  stage: TransformStage,
  message: string,
  cause?: unknown,
): Error => {
  const error = new Error(message)
  ;(error as Error & { stage?: TransformStage; cause?: unknown }).stage = stage
  ;(error as Error & { stage?: TransformStage; cause?: unknown }).cause = cause
  return error
}

export const sanitizeValidationOperators = (
  files: Record<string, unknown>,
): number => {
  const worksheetPathRegex = /^xl\/worksheets\/[^/]+\.xml$/
  let changedOperators = 0

  for (const [path, content] of Object.entries(files)) {
    if (!worksheetPathRegex.test(path) || typeof content !== "string") {
      continue
    }

    const sanitized = content.replace(
      /<(x14:)?dataValidation\b([^>]*?)>/g,
      (fullMatch, maybePrefix: string | undefined, attributes: string) => {
        if (!/\soperator="[^"]*"/.test(attributes)) {
          return fullMatch
        }

        changedOperators += 1
        const prefix = maybePrefix ?? ""
        const cleanedAttributes = attributes.replace(/\soperator="[^"]*"/g, "")
        return `<${prefix}dataValidation${cleanedAttributes}>`
      },
    )

    if (sanitized !== content) {
      files[path] = sanitized
    }
  }

  return changedOperators
}

const transformExcelToFortune = async (
  source: FileEvent | string,
  signal?: AbortSignal,
): Promise<{ sheets: FortuneSheet[]; diagnostics: TransformDiagnostics }> => {
  const sourceType = typeof source === "string" ? "url" : "file"
  let fileName = "Excel File"

  let excelFileBuffer
  if (typeof source === "string") {
    try {
      const url = new URL(source)
      const candidateName = url.pathname.split("/").pop()
      if (candidateName) {
        fileName = decodeURIComponent(candidateName)
      }
    } catch {
      // Ignore invalid URL parsing and keep default file name.
    }

    let response: Response
    try {
      response = await fetchWithRetry(source, signal)
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch the selected file"

      throw createStageError("fetch", message, error)
    }

    try {
      excelFileBuffer = await response.arrayBuffer()
    } catch (error) {
      throw createStageError("arrayBuffer", "Failed to read Excel file", error)
    }
  } else {
    const selectedFile = source.target.files?.[0]
    fileName = selectedFile?.name || fileName

    try {
      excelFileBuffer = await selectedFile?.arrayBuffer()
    } catch (error) {
      throw createStageError("arrayBuffer", "Failed to read Excel file", error)
    }

    if (!excelFileBuffer) {
      throw createStageError("arrayBuffer", "No Excel file buffer available")
    }
  }

  const bufferBytes =
    excelFileBuffer instanceof ArrayBuffer ? excelFileBuffer.byteLength : 0

  let files
  let sanitizedValidationOperators: number
  try {
    const excelFile = new File([excelFileBuffer], fileName || "workbook.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    files = await new HandleZip(excelFile).unzipFile()
    sanitizedValidationOperators = sanitizeValidationOperators(files)
  } catch (error) {
    throw createStageError("unzip", "Failed to unzip Excel workbook", error)
  }

  const fortuneFile = new FortuneFile(files, fileName)

  try {
    fortuneFile.Parse()
  } catch (error) {
    throw createStageError("parse", "Failed to parse Excel workbook", error)
  }

  let lsh
  try {
    lsh = fortuneFile.serialize()
  } catch (error) {
    throw createStageError(
      "serialize",
      "Failed to serialize workbook for viewer",
      error,
    )
  }

  const frozen = readFrozenPanes(files)
  const sheets: FortuneSheet[] = lsh.sheets.map((sheet: FortuneSheet) => {
    const pane = sheet.name ? frozen.get(sheet.name) : undefined
    return pane ? { ...sheet, frozen: pane } : sheet
  })

  return {
    sheets,
    diagnostics: {
      sourceType,
      fileName,
      bufferBytes,
      sheetCount: Array.isArray(lsh?.sheets) ? lsh.sheets.length : undefined,
      sanitizedValidationOperators,
    },
  }
}

export const normalizeCellString = (value: string) => {
  if (value === null || value === undefined) {
    return ""
  }
  // Excel quotePrefix often manifests as a leading apostrophe in exported values.
  const withoutQuotePrefix = value.startsWith("'") ? value.substring(1) : value

  // Decode common HTML entities so users see real symbols (e.g. &amp; -> &).
  return decode(withoutQuotePrefix)
}

// Process sheets to ensure column configuration is properly set for resizing
export const processSheets = (
  newSheets: FortuneSheet[],
  /**
   * `revealHidden` un-hides every hidden sheet, row and column — a supplier must
   * not be able to retract a row by hiding it, and a proof anchored in hidden
   * content has to be landable (Concept Part V, "Hidden rows, columns and sheets
   * — read, tag, surface"; Part VII hardening 2).
   *
   * IT HAPPENS HERE, IN THE VIEW MODEL, deliberately. The renderer's own
   * `showRowOrColumn` writes `config.rowhidden` on sheet data, which is under
   * `luckysheetfile` and therefore produces a change op and an undo entry;
   * rewriting the view model on its way in produces neither, and the workbook
   * this viewer loads is re-derived from the stored bytes every time. Which is
   * the invariant: the file is never modified — this is presentation state.
   *
   * That the content WAS hidden is not lost, only its concealment: the caller
   * badges the proof (see the declaration wrapper).
   */
  { revealHidden = false }: { revealHidden?: boolean } = {},
): (FortuneSheet & { name: string })[] => {
  return newSheets.map((sheet) => {
    const processedCelldata = sheet.celldata?.map((cell) => {
      if (
        cell.v &&
        typeof cell.v === "object" &&
        typeof cell.v.v === "string"
      ) {
        const normalized = normalizeCellString(cell.v.v)

        if (normalized !== cell.v.v) {
          return {
            ...cell,
            v: {
              ...cell.v,
              v: normalized,
            },
          }
        }
      }
      return cell
    })

    // Ensure defaultColWidth is set (required for Fortune Sheet to enable column resizing)
    const processedSheet = {
      ...sheet,
      name: normalizeSheetName(sheet.name),
      celldata: processedCelldata || sheet.celldata,
      images: undefined,
      defaultColWidth: sheet.defaultColWidth || 73, // Fortune Sheet default
      zoomRatio: 1, // Set zoom ratio to 1 (100%)
      ...(revealHidden ? { hide: 0 } : {}),
    }

    if (sheet.config) {
      processedSheet.config = {
        ...sheet.config,
        images: undefined,
        columnlen: sheet.config?.columnlen ? { ...sheet.config.columnlen } : {},
        customWidth: sheet.config.customWidth
          ? { ...sheet.config.customWidth }
          : {},
        ...(revealHidden ? { rowhidden: {}, colhidden: {} } : {}),
      }
    }

    return processedSheet
  })
}

/**
 * ADDITIVE HIGHLIGHT / SEARCH / JUMP API (Concept "Excel declarations and cell
 * evidence", Part VII · "App side: viewer additive, one adapter per flow").
 *
 * Every one of these is optional and every existing call site passes none of
 * them, so their behaviour is bit-for-bit what it was. Highlights are described
 * in workbook coordinates (sheet name + cell range) and composed by the caller:
 * one layer, set semantics, two independent uses (search marks, proof tints).
 */
export type ExcelViewerOverlayProps = {
  /** The WHOLE decoration layer, composed by the caller. `[]` clears it. */
  decorations?: RangeDecoration[]
  /** Where to put the reviewer. A new object = a new jump. */
  jump?: WorkbookJump | null
  /**
   * ⌘F / Ctrl+F was pressed inside the workbook. Wire it to your own search bar:
   * the browser's find is always prevented (it cannot see a canvas grid) and the
   * library ships no ⌘F dialog. Absent ⇒ exactly today's behaviour, the key is
   * swallowed.
   */
  onFind?: () => void
  /**
   * The workbook finished loading and remounted. Fired from the same post-load
   * lifecycle that re-applies column widths and the decoration layer, which is
   * the only moment a caller can read the fresh sheet list.
   */
  onLoad?: (surfaces: WorkbookSurface[]) => void
  /** See `processSheets` — un-hide hidden sheets/rows/columns, presentation only. */
  revealHidden?: boolean
}

export const ExcelJSViewer: React.FC<
  {
    file: string | FileEvent
    editable?: boolean
    workbookRef?: MutableRefObject<WorkbookInstance | null>
  } & ExcelViewerOverlayProps
  // Props are destructured in the body rather than in the parameter list so the
  // additive overlay props above do not reindent this whole component.
> = memo((props) => {
  const {
    file,
    editable = false,
    workbookRef: externalRef,
    decorations,
    jump = null,
    onFind,
    onLoad,
    revealHidden = false,
  } = props
  const internalRef = useRef<WorkbookInstance | null>(null)
  // Use a stable ref - prefer external, fallback to internal
  const workbookRef = externalRef ?? internalRef
  const [key, setKey] = useState(0)
  const [resetKey, setResetKey] = useState(0)
  const [sheets, setSheets] = useState<
    (FortuneSheet & { name: string })[] | undefined
  >(undefined)
  const [isLoadingSheets, setIsLoadingSheets] = useState(true)
  const [loadError, setLoadError] = useState<Error | null>(null)
  const { translate } = useTranslation()

  // The overlay's inputs, held in refs as well as props: the workbook is
  // REMOUNTED (key bump) on every file load, which drops the decoration layer
  // and swallows a jump asked for before the mount. Both therefore have to be
  // re-applied from the post-load lifecycle, and that code runs inside a
  // setTimeout where props are stale — so it reads the refs.
  const decorationsRef = useRef<RangeDecoration[] | undefined>(decorations)
  const pendingJumpRef = useRef<WorkbookJump | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const onFindRef = useRef(onFind)
  const onLoadRef = useRef(onLoad)
  useEffect(() => {
    onFindRef.current = onFind
    onLoadRef.current = onLoad
  }, [onFind, onLoad])

  // The landing still settling, if any. A new jump cancels it — two landings
  // would fight over the view — and so does unmounting.
  const cancelLandingRef = useRef<(() => void) | null>(null)
  const jumpGenerationRef = useRef(0)
  useEffect(() => () => cancelLandingRef.current?.(), [])

  /**
   * Put a range on screen: activate its sheet, then select it and scroll to it.
   *
   * The ref carries the CONTEXT OF THE RENDER THAT GENERATED IT, and the
   * workbook publishes its sheet list on its own first effect — so immediately
   * after a (re)mount `getAllSheets()` can still be empty. A jump asked for then
   * has to wait for the list, not be discarded.
   *
   * The sheet is activated FIRST and the range landed after, and the landing is
   * then held until the renderer has finished switching sheets: see
   * `landOnRange` for why one tick after the activation is not enough.
   */
  const applyJump = useCallback(
    (target: WorkbookJump) => {
      cancelLandingRef.current?.()
      cancelLandingRef.current = null
      const generation = ++jumpGenerationRef.current
      let attempts = 0
      const attempt = () => {
        // A newer jump has replaced this one while it waited for the sheets.
        if (generation !== jumpGenerationRef.current) return
        const workbook = workbookRef.current
        // Unmounted (React nulls the ref) — nothing to land on, and the next
        // load's post-load lifecycle will re-apply whatever is still pending.
        if (!workbook) return
        const sheets = workbook.getAllSheets()
        if (sheets.length === 0) {
          if (attempts >= 20) return
          attempts += 1
          window.setTimeout(attempt, 8)
          return
        }
        const sheet = sheets.find(
          (candidate) => candidate.name === target.sheet,
        )
        // An unknown sheet is an honest no-op, not a crash and not a guess: the
        // caller is the one that can say "not in this workbook".
        if (!sheet?.id) return
        workbook.activateSheet({ id: sheet.id })
        cancelLandingRef.current = landOnRange({
          workbook: () => workbookRef.current,
          // The active tab, read off the DOM: it is the committed render's
          // answer, and the API's `getSheet()` serializes the whole sheet. No
          // tab strip at all leaves nothing to wait for.
          isOnSheet: () => {
            const active = rootRef.current?.querySelector(
              ".luckysheet-sheets-item-active .luckysheet-sheets-item-name",
            )
            return !active || active.textContent === sheet.name
          },
          sheetId: sheet.id,
          range: target.range,
          select: target.select !== false,
          scrollbars: () => ({
            x:
              rootRef.current?.querySelector(".luckysheet-scrollbar-x") ?? null,
            y:
              rootRef.current?.querySelector(".luckysheet-scrollbar-y") ?? null,
          }),
          interruptions: rootRef.current,
        })
      }
      attempt()
    },
    [workbookRef],
  )

  /** Set the whole decoration layer, then consume any jump that is waiting. */
  const applyOverlay = useCallback(() => {
    if (!workbookRef.current) return
    applyRangeDecorations(workbookRef.current, decorationsRef.current ?? [])
    const waiting = pendingJumpRef.current
    if (waiting) {
      pendingJumpRef.current = null
      applyJump(waiting)
    }
  }, [applyJump, workbookRef])

  useEffect(() => {
    decorationsRef.current = decorations
    applyOverlay()
  }, [decorations, applyOverlay])

  useEffect(() => {
    if (!jump) return
    pendingJumpRef.current = jump
    applyOverlay()
  }, [jump, applyOverlay])

  useEffect(() => {
    if (editable) return

    const disableInlineEditor = () => {
      const editor = document.getElementById("luckysheet-rich-text-editor")
      if (editor) {
        // Remove editability + block interaction.
        editor.setAttribute("contenteditable", "false")
        ;(editor as HTMLElement).style.pointerEvents = "none"
      }

      const inputBox = document.querySelector(
        ".luckysheet-input-box",
      ) as HTMLElement | null
      if (inputBox) {
        inputBox.setAttribute("contenteditable", "false")
        inputBox.style.pointerEvents = "none"
      }
    }

    // Run once now, and also whenever Fortune Sheet re-creates the editor.
    disableInlineEditor()

    // const observer = new MutationObserver(() => disableInlineEditor())
    // observer.observe(document.body, { childList: true, subtree: true })
    // return () => observer.disconnect()
  }, [editable])

  // Fortune Sheet enables an inline editor when `allowEdit` is true.
  // We want to keep selection + double click behaviors, but prevent user typing
  // so that cell values only change through our external Q&A panel syncing.
  const preventUserInput = useCallback((e: React.SyntheticEvent) => {
    const native = e.nativeEvent as Event
    const type = native.type

    // Key events: block any Option/Alt-modified combos (dead keys like Option+6 -> ^)
    // while still allowing keyboard navigation.
    if (type === "keydown" || type === "keypress" || type === "keyup") {
      const ke = native as KeyboardEvent
      const key = ke.key

      // Keep Escape allowed so users can close any open editor UI.
      if (ke.altKey && key !== "Escape") {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      // ⌘F / Ctrl+F — ROUTED OUT, not allowlisted. It cannot simply be let
      // through: React attaches at the root, so the SyntheticEvent's
      // stopPropagation below also stops the native event, which is why a
      // window-level ⌘F listener on the screen can never hear the key while
      // focus is inside the workbook. So it is handled here and handed to the
      // caller's own bar. The default is always prevented — the browser's find
      // cannot see a canvas grid, and the library has no ⌘F dialog to open.
      // With no `onFind` this is exactly the old behaviour: swallowed.
      if ((ke.metaKey || ke.ctrlKey) && key.toLowerCase() === "f") {
        e.preventDefault()
        e.stopPropagation()
        if (type === "keydown") onFindRef.current?.()
        return
      }

      // ⌘C / Ctrl+C — let the library copy the selected cells. The grid is a
      // canvas, so its own copy is the only way text leaves it; swallowing the
      // key here left the reviewer no way to copy a cell value out. Read-only
      // means "cannot change the workbook", not "cannot read from it". Matched
      // on `code`, exactly as the library's own copy shortcut is.
      if ((ke.metaKey || ke.ctrlKey) && !ke.shiftKey && ke.code === "KeyC") {
        return
      }

      const allowed =
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "Tab" ||
        key === "Shift" ||
        key === "Meta" ||
        key === "Control" ||
        key === "Escape" ||
        key === "Enter" ||
        key === "PageUp" ||
        key === "PageDown" ||
        key === "Home" ||
        key === "End"

      if (allowed) return

      e.preventDefault()
      e.stopPropagation()
      return
    }

    // beforeinput/input: block any actual text insertion.
    // Dead keys sometimes bypass keydown prevention and surface here.
    if (type === "beforeinput" || type === "input") {
      const ie = native as InputEvent
      const inputType = ie.inputType

      // Allow non-textual input types only if you want (we keep it strict: block everything).
      // But keep Escape handling via key events above.
      if (
        inputType?.startsWith("insert") ||
        inputType?.startsWith("delete") ||
        inputType?.startsWith("format")
      ) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      e.preventDefault()
      e.stopPropagation()
      return
    }

    // composition events (IME / dead-keys): always block in read-only mode.
    if (
      type === "compositionstart" ||
      type === "compositionupdate" ||
      type === "compositionend"
    ) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    // Clipboard events.
    if (type === "paste" || type === "cut") {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  const preventPointerEdits = useCallback((e: React.SyntheticEvent) => {
    // Some Fortune Sheet editing UIs (like DataVerification dropdowns) are driven
    // by pointer events rather than keyboard input. When we're in non-editable
    // mode, block clicks on those overlays so they can't commit a value.
    const target = e.target as HTMLElement | null
    if (!target) return

    // allow clicks in this containers
    if (target.closest(".luckysheet-sheet-area")) {
      return
    }

    // Match Fortune Sheet's DataVerification / dropdown overlay containers.
    // We keep this intentionally broad but scoped to the workbook subtree.
    const selector =
      "[class*='data-verification'],[class*='dataVerification'],[class*='dataverification'],[class*='verification'],[class*='dropdown'],[class*='select']"

    if (target.closest(selector)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  useEffect(() => {
    if (!file) return

    const abortController = new AbortController()
    let isMounted = true

    const loadExcel = async () => {
      setIsLoadingSheets(true)
      setLoadError(null)
      setSheets(undefined)

      try {
        const result = await transformExcelToFortune(
          file,
          abortController.signal,
        )

        // Don't update state if component unmounted or request was aborted
        if (!isMounted || abortController.signal.aborted) return

        const processed = processSheets(result.sheets, { revealHidden })
        setSheets(processed)
        setKey((k) => k + 1)
        setIsLoadingSheets(false)

        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.info("ExcelJSViewer load diagnostics", result.diagnostics)
        }

        /**
         * Run a post-load step once the workbook is REALLY there.
         *
         * "There" is not "the ref is set": the component publishes its ref before
         * it has resolved which sheet is current and publishes its sheet list on
         * its own first effect, and every by-id call — `setColumnWidth`,
         * `activateSheet` — throws SHEET_NOT_FOUND out of the state updater until
         * that has happened. React computes the first update in a batch eagerly,
         * so the throw lands inside the call, surfaces as a render error, and the
         * ErrorBoundary turns the whole panel into "something went wrong". The old
         * fixed 1 ms delay simply hoped to win that race.
         *
         * Each step gets its own timer, so a throw in one cannot swallow the next.
         */
        const whenWorkbookReady = (
          step: (workbook: WorkbookInstance) => void,
        ) => {
          let waits = 0
          const attempt = () => {
            if (!isMounted) return
            const workbook = workbookRef.current
            if (!workbook || workbook.getAllSheets().length === 0) {
              if (waits >= 200) return
              waits += 1
              setTimeout(attempt, 8)
              return
            }
            step(workbook)
          }
          setTimeout(attempt, 1)
        }

        // Restore the file's own column/row sizes.
        whenWorkbookReady((workbook) => {
          for (const sheet of result.sheets) {
            const config = sheet.config
            workbook.setColumnWidth(config?.columnlen || {}, { id: sheet.id })
            workbook.setRowHeight(config?.rowlen || {}, { id: sheet.id })
          }
        })

        // THE REMOUNT RE-APPLICATION — the same post-load moment, right behind
        // the sizes, and the only one where the fresh instance exists: the key
        // bump above threw the decoration layer away with the old workbook. A
        // jump asked for before the load (a proof click on a screen that is still
        // fetching) waits in `pendingJumpRef` and lands here too.
        whenWorkbookReady(() => {
          applyOverlay()
          onLoadRef.current?.(
            processed.map((sheet, index) => ({
              ordinal: index + 1,
              name: sheet.name,
              // The file's OWN flag, before `revealHidden` cleared it — what was
              // hidden must stay knowable once it is on screen.
              hidden: result.sheets[index]?.hide === 1,
            })),
          )
        })
      } catch (error) {
        // Ignore cancellation only; surface all other errors instead of rendering a blank panel.
        if (abortController.signal.aborted || !isMounted) {
          return
        }

        const nextError =
          error instanceof Error
            ? error
            : new Error("Failed to load Excel preview")

        const stage =
          (
            nextError as Error & {
              stage?: TransformStage
            }
          ).stage ?? null

        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error("ExcelJSViewer failed to load workbook", {
            stage,
            error: nextError,
          })
        }
        // The visible error is a generic translated message; keep failures
        // diagnosable in production via Sentry (stage = transform step).
        Sentry.captureException(nextError, { extra: { stage } })
        setLoadError(nextError)
        setSheets(undefined)
        setIsLoadingSheets(false)
      }
    }

    loadExcel()

    return () => {
      isMounted = false
      abortController.abort()
    }
    // Note: workbookRef is a ref and should not trigger re-runs. Nor may
    // `applyOverlay` / `revealHidden` — a decoration change must never re-fetch
    // and re-parse the workbook; the overlay is re-applied by its own effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, resetKey])

  return (
    <ErrorBoundary
      resetKey={resetKey}
      fallback={(reset) => (
        <div className="flex h-full items-center justify-center bg-grey3">
          <div className="text-center">
            <h2 className="pb-M text-lg font-semibold">
              {translate("ERROR/SOMETHING_WENT_WRONG")}
            </h2>
            <p className="pb-L text-sm text-grey5">
              {translate("ERROR/UNEXPECTED_ERROR_OCCURED")}
            </p>
            <Button
              onClick={() => {
                setResetKey((prev) => prev + 1)
                setSheets(undefined)
                setLoadError(null)
                setIsLoadingSheets(true)
                reset()
              }}
              variant={"outline"}
            >
              {translate("QUESTIONNAIRE_ANSWERS/RELOAD")}
            </Button>
          </div>
        </div>
      )}
    >
      {isLoadingSheets ? (
        <div className="flex h-full items-center justify-center bg-grey3">
          <div className="text-center">
            <h2 className="pb-M text-lg font-semibold">
              {translate("QUESTIONNAIRE_ANSWERS/PREVIEW_TITLE")}
            </h2>
            <p className="text-sm text-grey5">
              {translate("QUESTIONNAIRE_ANSWERS/PREVIEW_DESCRIPTION")}
            </p>
          </div>
        </div>
      ) : loadError ? (
        <div className="flex h-full items-center justify-center bg-grey3">
          <div className="text-center">
            <h2 className="pb-M text-lg font-semibold">
              {translate("ERROR/SOMETHING_WENT_WRONG")}
            </h2>
            <p className="pb-L text-sm text-grey5">
              {translate("ERROR/EXCEL_LOAD_FAILED")}
            </p>
            <Button
              onClick={() => {
                setResetKey((prev) => prev + 1)
                setSheets(undefined)
                setLoadError(null)
                setIsLoadingSheets(true)
              }}
              variant={"outline"}
            >
              {translate("QUESTIONNAIRE_ANSWERS/RELOAD")}
            </Button>
          </div>
        </div>
      ) : sheets ? (
        <div
          ref={rootRef}
          className="h-full w-full"
          data-excel-viewer-root
          // Capture-phase handlers to intercept events before Fortune Sheet mutates state.
          onKeyDownCapture={preventUserInput}
          onKeyPressCapture={preventUserInput}
          onKeyUpCapture={preventUserInput}
          onBeforeInputCapture={preventUserInput}
          onInputCapture={preventUserInput}
          onPasteCapture={preventUserInput}
          onCutCapture={preventUserInput}
          onCompositionStartCapture={preventUserInput}
          onCompositionUpdateCapture={preventUserInput}
          onCompositionEndCapture={preventUserInput}
          onMouseDownCapture={preventPointerEdits}
          onClickCapture={preventPointerEdits}
        >
          {!editable ? (
            <style>
              {`
                [data-excel-viewer-root] #luckysheet-dataVerification-dropdown-btn {
                  display: none !important;
                  pointer-events: none !important;
                }

                [data-excel-viewer-root] .fortune-col-header-hover .header-arrow {
                  display: none !important;
                  pointer-events: none !important;
                }

                [data-excel-viewer-root] .fortune-context-menu.luckysheet-cols-menu {
                  display: none !important;
                  pointer-events: none !important;
                }

                [data-excel-viewer-root] .fortune-popover-backdrop.fortune-modal-container {
                  display: none !important;
                  pointer-events: none !important;
                }
              `}
            </style>
          ) : null}
          <Workbook
            key={key}
            data={sheets as unknown as WorkbookData}
            ref={workbookRef}
            toolbarItems={NO_ITEMS}
            cellContextMenu={NO_ITEMS}
            headerContextMenu={NO_ITEMS}
            showFormulaBar={false}
            // Important: keep this true so the double-click editor/popup still appears,
            // but input is blocked by the wrapper listeners above.
            allowEdit={true}
          />
        </div>
      ) : null}
    </ErrorBoundary>
  )
})
